<script setup>
// A SIGN-IN with Apple/Google that no account uses (ACCOUNT_IDENTITY_PLAN.md layer 1).
// Nothing was created. The person either signs in by email — and we attach the social
// identity to that account with the pending identity token — or creates a new account
// from the same token. Neither needs a second trip to the provider.
import LoginForm from '~/components/auth/LoginForm.vue';
import SocialLoginButtons from '~/components/auth/SocialLoginButtons.client.vue';

const props = defineProps({
  // {token, provider, email, isRelay} from the API's AccountNotFound answer
  pending: {
    type: Object,
    required: true,
  },
  redirectUrl: {
    type: String,
    default: '/',
  },
  authId: {
    type: String,
    default: null,
  },
});
const emit = defineEmits(['back']);

const router = useRouter();
const {externalLogin, externalLoginItem, externalLoginErrors, hasExternalLoginErrors, externalLoginLoading} = useAuthService();

const mode = ref(null); // null | 'link'
const providerName = computed(() => props.pending.provider === 'apple' ? 'Apple ID' : props.pending.provider === 'google' ? 'Google account' : 'account');
const providerShort = computed(() => props.pending.provider === 'apple' ? 'Apple' : props.pending.provider === 'google' ? 'Google' : 'this');

// They tried the OTHER provider from here and no account uses that one either. Say so
// and stop offering it: email-and-link and a new account are what is left. The first
// identity stays the pending one — it is what both remaining routes attach or create with.
const otherNotFound = ref(null); // null | the other provider's display name
const onOtherNotFound = (other) => {
  otherNotFound.value = other?.provider === 'apple' ? 'Apple ID' : other?.provider === 'google' ? 'Google account' : 'account';
};

// "Create a new account": same identity, intent signUp, no provider round-trip.
const createNew = async () => {
  const body = {intent: 'signUp', pendingIdentityToken: props.pending.token};
  if (props.authId) body.authId = props.authId;
  await externalLogin(body);
  if (hasExternalLoginErrors.value) return;
  if (externalLoginItem.value?.existingAccount) {
    const {setAlertMessage} = useAlertService();
    setAlertMessage({message: 'You already had a Tap Leagues account with this identity, so we signed you in.', color: 'success'});
  }
  if (externalLoginItem.value?.newAccount === true) {
    await router.push('/details-required?redirectUrl=' + encodeURIComponent(props.redirectUrl || '/'));
    return;
  }
  await router.push(props.redirectUrl || '/');
};
</script>

<template>
  <div class="anf" data-testid="social-account-not-found">
    <!-- Link: the normal email sign-in, then the identity is attached -->
    <template v-if="mode === 'link'">
      <div class="anf-head">
        <div class="anf-title">Sign in to link {{ providerShort }}</div>
        <div class="anf-sub">
          Sign in with the email you registered with. We'll attach your {{ providerName }} to that account, so
          {{ providerShort }} works next time.
        </div>
      </div>
      <login-form :hide-hero="true" :hide-social="true" :link-pending-token="pending.token"
                  :redirect-url="redirectUrl" :auth-id="authId"/>
      <div class="auth-foot"><a href="#" @click.prevent="mode = null">Back</a></div>
    </template>

    <!-- The question -->
    <template v-else>
      <div class="anf-head">
        <span class="anf-icon"><v-icon size="22">mdi-account-question-outline</v-icon></span>
        <div>
          <div class="anf-title">No Tap Leagues account uses this {{ providerName }} yet</div>
          <div class="anf-sub">
            <template v-if="pending.isRelay">
              Apple hid your email address, so we can't match it to an existing account.
            </template>
            <template v-else-if="pending.email">
              We looked for <b>{{ pending.email }}</b> and found no account.
            </template>
            If you already use Tap Leagues, sign in the way you usually do (email, Google or Apple) and we'll link
            {{ providerShort }} to that account. New here? Create an account.
          </div>
        </div>
      </div>

      <v-alert v-if="otherNotFound" type="info" variant="tonal" density="compact" class="mb-3"
               data-testid="anf-other-not-found">
        No Tap Leagues account uses that {{ otherNotFound }} either. Sign in with your email and we'll link
        {{ providerShort }}, or create a new account with your {{ providerName }}.
      </v-alert>

      <!-- show-all, or a field-scoped 400 renders nothing and the button looks inert -->
      <crud-error-display class="mb-3" :show-all="true" :errors="externalLoginErrors" v-if="externalLoginErrors"/>

      <v-btn color="primary" size="large" :block="true" class="mb-2" data-testid="anf-link"
             prepend-icon="mdi-email-outline" @click="mode = 'link'">
        Sign in with email and link {{ providerShort }}
      </v-btn>
      <!-- Registered with the OTHER provider (Google ↔ Apple): sign in with it and the
           blocked identity is linked to that account. -->
      <social-login-buttons v-if="!otherNotFound" class="mb-2" type="login" :hide-provider="pending.provider"
                            :link-pending-token="pending.token" :redirect-url="redirectUrl" :auth-id="authId"
                            @account-not-found="onOtherNotFound"/>
      <v-btn variant="outlined" size="large" :block="true" data-testid="anf-create" :loading="externalLoginLoading"
             prepend-icon="mdi-account-plus-outline" @click="createNew">
        Create a new account
      </v-btn>
      <div class="auth-foot"><a href="#" @click.prevent="emit('back')">Back</a></div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.anf-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.anf-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  background: rgba(var(--v-theme-primary), .08);
  color: rgb(var(--v-theme-primary));
}

.anf-title {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.2px;
  color: rgb(var(--v-theme-text));
  margin-bottom: 4px;
}

.anf-sub {
  font-size: 13px;
  line-height: 1.5;
  color: rgb(var(--v-theme-charcoal));
}
</style>
